import React, { useState, useEffect, useCallback } from 'react'
import { Plus, Pencil, Trash2, UserPlus, X, Search, Users, Check, ChevronDown, ChevronRight, Phone, Mail, MoreHorizontal, Merge } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Input, Label, Card, CardContent, Badge,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../ui/dropdown-menu'
import {
  getFamilias, createFamilia, updateFamilia, unificarCuentas, guardarResponsableDeFamilia,
  assignPlayerToFamilia, removePlayerFromFamilia,
  getAllPlayersWithGroups,
} from '../../../lib/supabase'
import { isVisiblePlayer } from '../../../lib/playerVisibility'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'
import { nombreFamilia } from '../../../lib/familias'
import { candidatosParaFamilia } from '../../../lib/candidatosFamilia'

// El responsable se captura por partes y se guarda en PERSONAS, no en familias.tutor_*:
// esas columnas son un espejo que la base ya no deja escribir (20260908b). Editar aqui
// corrige a la persona, y el espejo baja el cambio solo.
const initialForm = { nombre: '', resp_nombres: '', resp_apellidos: '', resp_telefono: '', resp_email: '', notas: '' }

const PagosFamilias = () => {
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const [familias, setFamilias] = useState([])
  const [unificarDestino, setUnificarDestino] = useState(null)
  const [unificando, setUnificando] = useState(false)
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const guard = useUnsavedGuard({ onClose: () => setFormOpen(false) })
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(initialForm)
  const [saving, setSaving] = useState(false)

  // Asignar jugador
  const [assignOpen, setAssignOpen] = useState(false)
  const assignGuard = useUnsavedGuard({ onClose: () => { setAssignOpen(false); setSelectedToAssign([]); setPlayerSearch('') } })
  const [assignFamilia, setAssignFamilia] = useState(null)
  const [allPlayers, setAllPlayers] = useState([])
  const [playerSearch, setPlayerSearch] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await getFamilias()
    setFamilias(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openCreate = () => {
    setEditing(null)
    setForm(initialForm)
    setFormOpen(true)
  }

  const openEdit = (f) => {
    setEditing(f)
    // El responsable es la persona principal del vinculo, ya partida en nombres y apellidos.
    const r = f.responsable
    setForm({
      nombre: f.nombre || '',
      resp_nombres: r?.nombres || '',
      resp_apellidos: r?.apellidos || '',
      resp_telefono: r?.telefono || '',
      resp_email: r?.email || '',
      notas: f.notas || '',
    })
    setFormOpen(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.nombre.trim()) return
    setSaving(true)
    const datosFamilia = { nombre: form.nombre, notas: form.notas }
    const { data: guardada, error } = editing
      ? await updateFamilia(editing.id, datosFamilia)
      : await createFamilia(datosFamilia)
    if (error) { setSaving(false); await alert('Error', error.message || 'No se pudo guardar'); return }
    // El responsable va a `personas`: si la familia ya tiene principal se corrige a esa
    // persona; si no, se crea y se liga. Es la unica via de escritura que queda.
    const { error: errResp } = await guardarResponsableDeFamilia(editing?.id || guardada?.id, {
      nombres: form.resp_nombres, apellidos: form.resp_apellidos,
      telefono: form.resp_telefono, email: form.resp_email,
    })
    setSaving(false)
    if (errResp) { await alert('Error', errResp.message || 'La familia se guardó, pero no el responsable'); return }
    guard.resetDirty()
    setFormOpen(false)
    load()
  }

  const handleDelete = async (f) => {
    if (!await confirm('Desactivar familia', `Desactivar "${f.nombre}"? Los jugadores se desvincularan.`, { destructive: true })) return
    // Desvincular jugadores
    for (const j of (f.jugadores || [])) {
      await removePlayerFromFamilia(j.id)
    }
    await updateFamilia(f.id, { activo: false })
    load()
  }

  // Unificar: los hermanos que quedaron en familias separadas se juntan en una.
  // Mueve tambien saldo a favor, solicitudes de pago y consentimientos -- si solo se
  // movieran los jugadores, el dinero quedaria apuntando a una familia desactivada.
  const handleUnificar = async (origen) => {
    if (!unificarDestino || !origen) return
    const ok = await confirm(
      'Unificar familias',
      `Los jugadores de "${origen.nombre}" pasan a "${unificarDestino.nombre}", junto con su saldo a favor, ` +
      `solicitudes de pago y consentimientos. "${origen.nombre}" queda desactivada.`,
      { destructive: true }
    )
    if (!ok) return
    setUnificando(true)
    const { data, error } = await unificarCuentas(unificarDestino.id, [origen.id])
    setUnificando(false)
    if (error) { await alert('Error', error.message); return }
    setUnificarDestino(null)
    await alert(
      'Listo',
      `Se movieron ${data.jugadores} jugador(es)` +
      (data.saldos ? `, ${data.saldos} saldo(s) a favor` : '') +
      (data.consentimientos ? `, ${data.consentimientos} consentimiento(s)` : '') +
      (data.responsables ? `, ${data.responsables} responsable(s)` : '') +
      `. ${unificarDestino.responsable?.nombre_completo ? `${unificarDestino.responsable.nombre_completo} sigue como responsable.` : ''}`
    )
    load()
  }

  // Candidatas a unificar, con las probables hermanas arriba. La senal es la misma
  // que en el alta de jugadores: apellidos completos iguales o el mismo telefono.
  const apellidosDe = (nombre) => {
    const partes = String(nombre || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().trim().split(/\s+/).filter(Boolean)
    return partes.length >= 2 ? partes.slice(-2).join(' ') : ''
  }
  const soloDigitos = (t) => String(t || '').replace(/\D/g, '')

  const candidatasUnificar = (() => {
    if (!unificarDestino) return []
    const apellidosDestino = new Set(
      (unificarDestino.jugadores || []).map((j) => apellidosDe(j.nombre_completo)).filter(Boolean)
    )
    const telDestino = soloDigitos(unificarDestino.responsable?.telefono)
    return familias
      .filter((f) => f.id !== unificarDestino.id)
      .map((f) => {
        const compartApellido = (f.jugadores || []).some((j) => apellidosDestino.has(apellidosDe(j.nombre_completo)))
        const mismoTel = telDestino.length >= 10 && soloDigitos(f.responsable?.telefono) === telDestino
        return { ...f, sugerida: compartApellido || mismoTel }
      })
      .sort((a, b) => (b.sugerida ? 1 : 0) - (a.sugerida ? 1 : 0) || (a.nombre || '').localeCompare(b.nombre || ''))
  })()

  // Asignar jugador flow
  const openAssign = async (familia) => {
    setAssignFamilia(familia)
    setPlayerSearch('')
    assignGuard.resetDirty()
    const { data } = await getAllPlayersWithGroups()
    setAllPlayers((data || []).filter(isVisiblePlayer))
    setAssignOpen(true)
  }

  const [selectedToAssign, setSelectedToAssign] = useState([])
  const [assigning, setAssigning] = useState(false)

  const handleConfirmAssign = async () => {
    if (!selectedToAssign.length) return
    setAssigning(true)
    const fallidos = []
    for (const jugadorId of selectedToAssign) {
      const { error } = await assignPlayerToFamilia(jugadorId, assignFamilia.id)
      if (error) {
        const j = allPlayers.find((p) => p.id === jugadorId)
        fallidos.push({ nombre: j?.nombre_completo || 'Un jugador', error })
      }
    }
    setAssigning(false)

    // Antes se descartaba el resultado: si fallaba, la pantalla se refrescaba igual y
    // el usuario no se enteraba de nada. Un fallo silencioso se lee como "no sirve".
    if (fallidos.length) {
      await alert(
        fallidos.length === 1 ? 'No se pudo agregar' : `No se pudieron agregar ${fallidos.length}`,
        fallidos.map((f) => `${f.nombre}: ${f.error.message}`).join(String.fromCharCode(10))
      )
    }
    assignGuard.resetDirty()
    setSelectedToAssign([])
    setPlayerSearch('')
    const { data } = await getFamilias()
    setFamilias(data || [])
    const updated = (data || []).find((f) => f.id === assignFamilia.id)
    if (updated) setAssignFamilia(updated)
  }

  const togglePlayerSelection = (playerId) => {
    assignGuard.markDirty()
    setSelectedToAssign((prev) =>
      prev.includes(playerId) ? prev.filter((id) => id !== playerId) : [...prev, playerId]
    )
  }

  const handleRemovePlayer = async (jugadorId) => {
    if (!await confirm('Quitar jugador', 'Quitar este jugador de la familia?', { destructive: true })) return
    const { error } = await removePlayerFromFamilia(jugadorId)
    if (error) { await alert('Error', error.message || 'No se pudo quitar el jugador'); return }
    const { data } = await getFamilias()
    setFamilias(data || [])
    const updated = (data || []).find((f) => f.id === assignFamilia?.id)
    if (updated) setAssignFamilia(updated)
  }

  const filteredPlayers = candidatosParaFamilia({
    jugadores: allPlayers,
    familias,
    familiaActualId: assignFamilia?.id || null,
    busqueda: playerSearch,
  })

  const [expandedFamilia, setExpandedFamilia] = useState(null)
  const [searchFilter, setSearchFilter] = useState('')

  const filteredFamilias = familias.filter((f) => {
    if (!searchFilter) return true
    const t = searchFilter.toLowerCase()
    return f.nombre?.toLowerCase().includes(t) ||
      f.responsable?.nombre_completo?.toLowerCase().includes(t) ||
      (f.jugadores || []).some((j) => j.nombre_completo?.toLowerCase().includes(t))
  })

  return (
    <FeaturedLayout
      title="Familias"
      subtitle="Quién pertenece a quién: agrupa a los hermanos bajo un mismo tutor, un cobro y un consentimiento por familia"
      refreshAction={load}
    >
      <div className="mb-4 flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar familia, tutor o jugador..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground hidden sm:block">
          <span className="font-medium text-foreground">{filteredFamilias.length}</span> familias
        </span>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" /> Nueva familia
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando...</p>
      ) : familias.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-4">No hay familias registradas</p>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> Crear primera familia</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Responsable</TableHead>
                <TableHead>Jugadores</TableHead>
                <TableHead className="hidden md:table-cell">Contacto</TableHead>
                <TableHead className="w-36"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFamilias.map((f) => {
                const isExpanded = expandedFamilia === f.id
                const jugadores = f.jugadores || []
                return (
                  <React.Fragment key={f.id}>
                    <TableRow
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setExpandedFamilia(isExpanded ? null : f.id)}
                    >
                      <TableCell>
                        {isExpanded
                          ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        }
                      </TableCell>
                      <TableCell className="font-medium">{nombreFamilia(f)}</TableCell>
                      {/* Quiénes son. Antes aquí iba el tutor, pero desde que la fila se
                          titula con SU nombre eso repetía la misma palabra dos veces. Los
                          jugadores son lo que de verdad distingue una familia de otra --
                          y quien responde por sí mismo se reconoce porque el título y el
                          único jugador coinciden. */}
                      <TableCell className="text-sm">
                        {jugadores.length
                          ? jugadores.map((j) => j.nombre_completo).filter(Boolean).join(', ')
                          : <span className="text-muted-foreground italic">Sin jugadores</span>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                          {f.responsable?.telefono && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{f.responsable.telefono}</span>}
                          {f.responsable?.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{f.responsable.email}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => openAssign(f)}>
                            <UserPlus className="h-3.5 w-3.5 sm:mr-1.5" />
                            <span className="hidden sm:inline text-xs">Agregar</span>
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Más acciones">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEdit(f)}>
                                <Pencil className="h-4 w-4" />
                                Editar familia
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setUnificarDestino(f)}>
                                <Merge className="h-4 w-4" />
                                Unificar otra familia aquí
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDelete(f)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                Eliminar familia
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Expanded: jugadores */}
                    {isExpanded && (
                      <TableRow className="bg-muted/30">
                        <TableCell></TableCell>
                        <TableCell colSpan={5}>
                          <div className="py-2 space-y-2">
                            {jugadores.length === 0 ? (
                              <p className="text-xs text-muted-foreground italic">Sin jugadores asignados</p>
                            ) : (
                              <div className="space-y-1">
                                {jugadores.map((j) => (
                                  <div key={j.id} className="flex items-center justify-between py-1.5 px-3 rounded border bg-background">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium">{j.nombre_completo}</span>
                                      {j.numero && <Badge variant="outline" className="text-[10px]">#{j.numero}</Badge>}
                                      <Badge className={`text-[10px] capitalize ${j.estatus === 'Activo' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                        {j.estatus || 'Sin estatus'}
                                      </Badge>
                                      {j.genero && <span className="text-xs text-muted-foreground">{j.genero}</span>}
                                    </div>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => handleRemovePlayer(j.id)} title="Quitar de la familia">
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openAssign(f)}>
                              <UserPlus className="h-3 w-3 mr-1" /> Agregar jugador
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Form Modal */}
      <Dialog open={formOpen} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar familia' : 'Nueva familia'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre de la familia *</Label>
              <Input value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Familia Martínez o Juan Pérez" required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="resp_nombres">Nombre(s) del responsable</Label>
                <Input id="resp_nombres" value={form.resp_nombres} onChange={(e) => setForm((p) => ({ ...p, resp_nombres: e.target.value }))} placeholder="Mónica" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="resp_apellidos">Apellidos</Label>
                <Input id="resp_apellidos" value={form.resp_apellidos} onChange={(e) => setForm((p) => ({ ...p, resp_apellidos: e.target.value }))} placeholder="Paterno y materno" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="resp_telefono">Teléfono del responsable</Label>
                <Input id="resp_telefono" value={form.resp_telefono} onChange={(e) => setForm((p) => ({ ...p, resp_telefono: e.target.value }))} placeholder="10 dígitos" type="tel" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="resp_email">Correo del responsable</Label>
                <Input id="resp_email" value={form.resp_email} onChange={(e) => setForm((p) => ({ ...p, resp_email: e.target.value }))} placeholder="correo@ejemplo.com" type="email" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => guard.onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
            </DialogFooter>
          </form>
          {guard.DiscardDialog}
        </DialogContent>
      </Dialog>

      {/* Assign Player Modal */}
      <Dialog open={assignOpen} onOpenChange={assignGuard.onOpenChange}>
        <DialogContent {...assignGuard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar jugadores a {assignFamilia?.nombre}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Current members */}
            {(assignFamilia?.jugadores || []).length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Miembros actuales</Label>
                {(assignFamilia?.jugadores || []).map((j) => (
                  <div key={j.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/50 text-sm">
                    <span>{j.nombre_completo}</span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive" onClick={() => handleRemovePlayer(j.id)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar jugador..."
                value={playerSearch}
                onChange={(e) => setPlayerSearch(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>

            {playerSearch && (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {filteredPlayers.map((p) => {
                  const isSelected = selectedToAssign.includes(p.id)
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between border ${isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted border-transparent'}`}
                      onClick={() => togglePlayerSelection(p.id)}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`h-4 w-4 rounded border flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                          {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <span>
                          {p.nombre_completo}
                          {p.familiaActual && (
                            <span className="ml-2 text-xs text-amber-700">
                              hoy en {p.familiaActual.nombre} — se movera
                            </span>
                          )}
                        </span>
                      </div>
                      {p.numero && <Badge variant="outline" className="text-xs">#{p.numero}</Badge>}
                    </button>
                  )
                })}
                {filteredPlayers.length === 0 && (
                  <p className="text-muted-foreground text-xs text-center py-2">
                    {allPlayers.length > 0 ? 'No se encontraron jugadores sin familia' : 'No hay jugadores'}
                  </p>
                )}
              </div>
            )}

            {selectedToAssign.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">
                  {selectedToAssign.length} jugador(es) seleccionado(s)
                </p>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setSelectedToAssign([])}>
                    Limpiar
                  </Button>
                  <Button size="sm" onClick={handleConfirmAssign} disabled={assigning}>
                    {assigning ? 'Agregando...' : `Agregar ${selectedToAssign.length}`}
                  </Button>
                </div>
              </div>
            )}
          </div>
          {assignGuard.DiscardDialog}
        </DialogContent>
      </Dialog>
      {/* Unificar familias. Se sugieren primero las que comparten apellidos o teléfono
          con la familia destino: son las que casi siempre son la misma. */}
      <Dialog open={!!unificarDestino} onOpenChange={(v) => !v && setUnificarDestino(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Unificar en {unificarDestino?.nombre}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Elige la familia que se va a juntar aquí. Sus jugadores, saldo a favor, solicitudes
            de pago y consentimientos pasan a <strong>{unificarDestino?.nombre}</strong>, y esa
            familia queda desactivada.
          </p>
          <div className="max-h-72 overflow-y-auto space-y-1.5">
            {candidatasUnificar.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No hay otras familias para unificar.
              </p>
            )}
            {candidatasUnificar.map((f) => (
              <button
                key={f.id}
                type="button"
                disabled={unificando}
                onClick={() => handleUnificar(f)}
                className="w-full text-left rounded-md border px-3 py-2 hover:bg-muted disabled:opacity-50"
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">{nombreFamilia(f)}</span>
                  {f.sugerida && <Badge variant="outline" className="text-[10px]">Probable hermano</Badge>}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {f.responsable?.nombre_completo || 'Sin responsable'}
                  {f.responsable?.telefono && ` · ${f.responsable.telefono}`}
                  {(f.jugadores || []).length > 0 && ` · ${(f.jugadores || []).map((j) => j.nombre_completo).join(', ')}`}
                </span>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnificarDestino(null)} disabled={unificando}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default PagosFamilias
